/**
 * Decision Rights Type System
 *
 * Models the three-axis question at the heart of governance:
 *   1. What is being decided?  → domain (CanonicalSessionType)
 *   2. What kind of decision?  → decisionClass (CanonicalDecisionClassKind)
 *   3. Who decides, and how?   → owner + allowedExecutionModes + delegationConstraints
 *
 * Plus the meta-level question:
 *   Who decides who decides?   → metaAssignment
 *
 * Decision rights live on a cycle (DecisionRightsConfiguration). They are
 * resolved at runtime by the evaluator functions below, which operate on
 * pure value objects and have no side effects.
 *
 * Relationship to existing types:
 * - CanonicalSessionType, CanonicalDecisionClassKind  → session.ts
 * - DecisionMode (INDIVIDUAL | DELEGATED | DEMOCRATIC)  → decisionInstance.ts
 *   DecisionExecutionMode is the canonical successor; DecisionMode is kept for compat.
 * - PermissionAction decision_rights.*  → permissions.ts
 */

import type { CanonicalSessionType, CanonicalDecisionClassKind } from './session';

// ============================================================================
// Execution Mode — How the right-holder exercises their authority
// ============================================================================

/**
 * How a decision right is exercised.
 *
 * - solo:     The right-holder acts unilaterally. No session required.
 * - delegate: The right-holder passes authority to another named principal.
 *             The delegate exercises the right, not the original holder.
 * - session:  The right-holder convenes a structured session. Participants
 *             contribute; the right-holder (or their designate) commits the
 *             outcome. The session does not override the right — it IS the
 *             mechanism of exercising it.
 *
 * Convergence decisions MUST use 'session'; the other two modes are invalid
 * for that class (enforced by DECISION_CLASS_EXECUTION_CONSTRAINTS).
 */
export type DecisionExecutionMode = 'solo' | 'delegate' | 'session';

export const DECISION_EXECUTION_MODE_LABELS: Record<DecisionExecutionMode, string> = {
  solo:     'Individual decides',
  delegate: 'Individual delegates',
  session:  'Individual holds a session to decide',
};

// ============================================================================
// Owner Binding — Who holds a decision right
// ============================================================================

/**
 * How an owner is identified.
 *
 * - ROLE_IN_SCOPE:       Any user holding a specified role within the scope.
 * - PRINCIPAL_IN_SCOPE:  A specific named user, regardless of role.
 * - ROLE_AND_PRINCIPAL:  A specific user who ALSO holds the specified role.
 *                        Stricter than either alone.
 */
export type OwnerBindingType = 'ROLE_IN_SCOPE' | 'PRINCIPAL_IN_SCOPE' | 'ROLE_AND_PRINCIPAL';

export interface DecisionRightOwner {
  bindingType: OwnerBindingType;
  /** Required for ROLE_IN_SCOPE and ROLE_AND_PRINCIPAL. Capability ID or role name. */
  roleId?: string;
  /** Required for PRINCIPAL_IN_SCOPE and ROLE_AND_PRINCIPAL. User ID. */
  principalId?: string;
}

// ============================================================================
// Delegation Constraints — Rules that govern how a right may be transferred
// ============================================================================

/**
 * Constraints that govern runtime delegation of a decision right.
 * A right without DelegationConstraints is not delegatable.
 */
export interface DelegationConstraints {
  /**
   * Roles that may receive a delegated right. At least one of
   * allowedRoleIds or allowedPrincipalIds must be non-empty.
   */
  allowedRoleIds?: string[];

  /**
   * Specific principals (user IDs) that may receive a delegated right.
   */
  allowedPrincipalIds?: string[];

  /**
   * How many times the right may be re-delegated downstream.
   * 0 = the original holder may delegate once, but the delegate may not re-delegate.
   * 1 = one level of re-delegation is permitted, and so on.
   */
  maxDepth: number;

  /**
   * Whether the delegation is effective immediately (false) or only
   * after the target explicitly accepts it (true).
   */
  requiresAcceptance: boolean;
}

// ============================================================================
// Decision Right — The atomic unit of governance assignment
// ============================================================================

/**
 * A single decision right assigned within a cycle.
 *
 * Each right fully specifies:
 * - WHAT is being decided (domain + decisionClass)
 * - WITHIN what scope (scopeSelector)
 * - BY WHOM (owner)
 * - USING WHICH MODES (allowedExecutionModes)
 * - UNDER WHAT DELEGATION RULES (delegationConstraints, if any)
 * - WHO MAY REASSIGN IT (metaAssignment)
 */
export interface DecisionRight {
  id: string;

  /** The subject domain of the decision. */
  domain: CanonicalSessionType;

  /**
   * The kind of judgment being made. Together with domain, this fully
   * characterizes what the decision produces.
   *
   * Examples:
   *   domain=items,     decisionClass=selection      → choose which items are in scope
   *   domain=items,     decisionClass=force_ranking  → rank items strictly 1-N
   *   domain=values,    decisionClass=convergence    → resolve disputed attribute values
   *   domain=criteria,  decisionClass=selection      → choose which attributes to use
   *   domain=scales,    decisionClass=range_selection → define value scales for attributes
   */
  decisionClass: CanonicalDecisionClassKind;

  /**
   * Scope selector within the cycle. Typically '*' (all), a track ID, an
   * attribute key, or an item category. Scoped rights override '*' rights
   * for matching scope.
   */
  scopeSelector: string;

  /** The principal or role that holds this right. */
  owner: DecisionRightOwner;

  /**
   * Which execution modes the owner may use to exercise this right.
   * Must be consistent with DECISION_CLASS_EXECUTION_CONSTRAINTS.
   * Use validateDecisionRight() before persisting.
   */
  allowedExecutionModes: DecisionExecutionMode[];

  /**
   * Rules governing delegation. When undefined, this right is not delegatable.
   * Delegation produces a DelegatedDecisionRight rather than mutating this record.
   */
  delegationConstraints?: DelegationConstraints;

  /**
   * Who may reassign (change the owner of) this right within the cycle.
   * This is the meta-level answer to "who decides who decides."
   *
   * Typically bound to the Cycle Owner role, but may be configured more
   * narrowly (e.g., only a named principal may reassign criteria selection).
   */
  metaAssignment: DecisionRightOwner;

  /** When this right was assigned. */
  assignedAt?: Date;
  /** Who made the assignment. */
  assignedBy?: string;
}

// ============================================================================
// Delegated Decision Right — Runtime delegation record
// ============================================================================

/**
 * A runtime delegation of a decision right. Created when an owner delegates
 * to another principal. Does not mutate the original DecisionRight.
 */
export interface DelegatedDecisionRight {
  id: string;
  /** The source right being delegated. */
  sourceRightId: string;
  /** The principal receiving the delegation. */
  delegatePrincipalId: string;
  /**
   * Execution modes the delegate may use. Must be a subset of
   * the source right's allowedExecutionModes.
   */
  allowedExecutionModes: DecisionExecutionMode[];
  /**
   * Depth of this delegation in the chain (1 = direct from original holder).
   * May not exceed source right's delegationConstraints.maxDepth.
   */
  depth: number;
  /** Whether the delegate has accepted (when requiresAcceptance is true). */
  accepted: boolean;
  acceptedAt?: Date;
  delegatedAt: Date;
  delegatedBy: string;
  /** When this delegation expires (undefined = lasts for the cycle). */
  expiresAt?: Date;
  /** Whether this delegation has been revoked. */
  revoked: boolean;
  revokedAt?: Date;
  revokedBy?: string;
}

// ============================================================================
// Decision Rights Configuration — Cycle-level container
// ============================================================================

/**
 * All decision rights configured for a cycle.
 * Stored on the cycle as a versioned configuration.
 */
export interface DecisionRightsConfiguration {
  cycleId: string;
  rights: DecisionRight[];
  delegations: DelegatedDecisionRight[];
  version: number;
  configuredAt: Date;
  configuredBy: string;
}

// ============================================================================
// Execution Mode Constraints by Decision Class
// ============================================================================

/**
 * Constraints on which execution modes are valid for each decision class.
 * Validated at configuration time via validateDecisionRight().
 *
 * Convergence is the only class with a required mode — it demands session
 * because resolving disputed values inherently requires multiple participants.
 */
export const DECISION_CLASS_EXECUTION_CONSTRAINTS: Record<
  CanonicalDecisionClassKind,
  {
    allowedModes: DecisionExecutionMode[];
    requiredModes?: DecisionExecutionMode[];
    note?: string;
  }
> = {
  selection: {
    allowedModes: ['solo', 'delegate', 'session'],
  },
  range_selection: {
    allowedModes: ['solo', 'delegate', 'session'],
  },
  convergence: {
    allowedModes: ['session'],
    requiredModes: ['session'],
    note: 'Convergence requires multiple participants and cannot be exercised solo or by delegation to a single actor.',
  },
  force_ranking: {
    allowedModes: ['solo', 'delegate', 'session'],
  },
  review: {
    allowedModes: ['solo', 'delegate', 'session'],
  },
};

// ============================================================================
// Validation
// ============================================================================

export interface DecisionRightValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validates a DecisionRight before it is persisted to a cycle.
 * Checks execution mode compatibility with the decision class, and
 * ensures delegation constraints are structurally sound.
 */
export function validateDecisionRight(right: DecisionRight): DecisionRightValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  const constraints = DECISION_CLASS_EXECUTION_CONSTRAINTS[right.decisionClass];

  // Every specified mode must be allowed for this class
  for (const mode of right.allowedExecutionModes) {
    if (!constraints.allowedModes.includes(mode)) {
      errors.push(
        `Execution mode '${mode}' is not allowed for decision class '${right.decisionClass}'.` +
          (constraints.note ? ` ${constraints.note}` : '')
      );
    }
  }

  // Required modes must be present
  for (const required of constraints.requiredModes ?? []) {
    if (!right.allowedExecutionModes.includes(required)) {
      errors.push(
        `Decision class '${right.decisionClass}' requires execution mode '${required}' to be listed.` +
          (constraints.note ? ` ${constraints.note}` : '')
      );
    }
  }

  // Must allow at least one mode
  if (right.allowedExecutionModes.length === 0) {
    errors.push('At least one execution mode must be specified.');
  }

  // Owner binding structural check
  const ownerErr = validateOwnerBinding(right.owner, 'owner');
  errors.push(...ownerErr);

  const metaErr = validateOwnerBinding(right.metaAssignment, 'metaAssignment');
  errors.push(...metaErr);

  // Delegation constraints structural check
  if (right.delegationConstraints) {
    const dc = right.delegationConstraints;
    if (!dc.allowedRoleIds?.length && !dc.allowedPrincipalIds?.length) {
      errors.push(
        'delegationConstraints must specify at least one allowedRoleId or allowedPrincipalId.'
      );
    }
    if (dc.maxDepth < 0) {
      errors.push('delegationConstraints.maxDepth must be >= 0.');
    }
    if (!right.allowedExecutionModes.includes('delegate')) {
      warnings.push(
        'delegationConstraints are set but execution mode "delegate" is not in allowedExecutionModes. ' +
          'Delegation will never be offered to the UI unless "delegate" is added.'
      );
    }
  } else if (right.allowedExecutionModes.includes('delegate')) {
    errors.push(
      'Execution mode "delegate" is listed but no delegationConstraints are defined. ' +
        'Add delegationConstraints to specify who may receive the delegation.'
    );
  }

  return { valid: errors.length === 0, errors, warnings };
}

function validateOwnerBinding(owner: DecisionRightOwner, field: string): string[] {
  const errors: string[] = [];
  if (
    (owner.bindingType === 'ROLE_IN_SCOPE' || owner.bindingType === 'ROLE_AND_PRINCIPAL') &&
    !owner.roleId
  ) {
    errors.push(`${field}.roleId is required for binding type '${owner.bindingType}'.`);
  }
  if (
    (owner.bindingType === 'PRINCIPAL_IN_SCOPE' || owner.bindingType === 'ROLE_AND_PRINCIPAL') &&
    !owner.principalId
  ) {
    errors.push(`${field}.principalId is required for binding type '${owner.bindingType}'.`);
  }
  return errors;
}

// ============================================================================
// Resolution
// ============================================================================

/**
 * Finds the decision right for a given domain and decision class within a cycle's
 * configuration. Scoped rights (non-'*' scopeSelector) take precedence over
 * wildcard rights.
 */
export function resolveDecisionRight(
  config: DecisionRightsConfiguration,
  domain: CanonicalSessionType,
  decisionClass: CanonicalDecisionClassKind,
  scopeSelector?: string
): DecisionRight | undefined {
  const candidates = config.rights.filter(
    (r) => r.domain === domain && r.decisionClass === decisionClass
  );
  if (candidates.length === 0) return undefined;

  // Prefer an exact scope match; fall back to wildcard
  if (scopeSelector && scopeSelector !== '*') {
    const scoped = candidates.find((r) => r.scopeSelector === scopeSelector);
    if (scoped) return scoped;
  }
  return candidates.find((r) => r.scopeSelector === '*') ?? candidates[0];
}

// ============================================================================
// Evaluator — CheckDecisionRight
// ============================================================================

export interface DecisionRightCheckContext {
  userId: string;
  /**
   * Capability IDs or role IDs the user holds in the cycle scope.
   * Compared against owner.roleId when binding type involves ROLE_IN_SCOPE.
   */
  userRoleIds: string[];
}

export interface DecisionRightCheckResult {
  allowed: boolean;
  reason?: string;
  /** Whether the user may delegate this right to another actor. */
  canDelegate: boolean;
  /** Whether the user may reassign the right owner (meta-assignment). */
  canReassign: boolean;
}

/**
 * Evaluates whether a user may exercise a decision right using a specific mode.
 *
 * This is a pure function — it does not query the database.
 * The caller is responsible for loading the DecisionRight and the user's roles.
 */
export function checkDecisionRight(
  right: DecisionRight,
  context: DecisionRightCheckContext,
  requestedMode: DecisionExecutionMode
): DecisionRightCheckResult {
  // 1. Does the user match the owner binding?
  const ownerMatch = matchesOwnerBinding(right.owner, context);
  if (!ownerMatch) {
    return {
      allowed: false,
      reason: 'User does not hold this decision right.',
      canDelegate: false,
      canReassign: matchesOwnerBinding(right.metaAssignment, context),
    };
  }

  // 2. Is the requested mode in the allowed set?
  if (!right.allowedExecutionModes.includes(requestedMode)) {
    return {
      allowed: false,
      reason: `Execution mode '${requestedMode}' is not permitted for this right. Allowed: ${right.allowedExecutionModes.join(', ')}.`,
      canDelegate: right.allowedExecutionModes.includes('delegate') && !!right.delegationConstraints,
      canReassign: matchesOwnerBinding(right.metaAssignment, context),
    };
  }

  return {
    allowed: true,
    canDelegate: right.allowedExecutionModes.includes('delegate') && !!right.delegationConstraints,
    canReassign: matchesOwnerBinding(right.metaAssignment, context),
  };
}

// ============================================================================
// Evaluator — CheckDelegation
// ============================================================================

export interface DelegationCheckResult {
  allowed: boolean;
  reason?: string;
}

/**
 * Evaluates whether a right-holder may delegate to a target principal.
 *
 * Pure function; caller provides the resolved right and both contexts.
 */
export function checkCanDelegate(
  right: DecisionRight,
  delegatorContext: DecisionRightCheckContext,
  targetPrincipalId: string,
  targetRoleIds: string[],
  currentDepth: number = 0
): DelegationCheckResult {
  if (!right.delegationConstraints) {
    return { allowed: false, reason: 'This decision right is not delegatable.' };
  }

  if (!matchesOwnerBinding(right.owner, delegatorContext)) {
    return { allowed: false, reason: 'Only the right-holder may delegate.' };
  }

  if (!right.allowedExecutionModes.includes('delegate')) {
    return { allowed: false, reason: 'Delegation mode is not permitted for this right.' };
  }

  const { allowedRoleIds, allowedPrincipalIds, maxDepth } = right.delegationConstraints;

  if (currentDepth > maxDepth) {
    return {
      allowed: false,
      reason: `Delegation depth ${currentDepth} exceeds the maximum of ${maxDepth}.`,
    };
  }

  const targetMatchesRole =
    allowedRoleIds?.some((roleId) => targetRoleIds.includes(roleId)) ?? false;
  const targetMatchesPrincipal =
    allowedPrincipalIds?.includes(targetPrincipalId) ?? false;

  if (!targetMatchesRole && !targetMatchesPrincipal) {
    return {
      allowed: false,
      reason: 'Target principal does not match the allowed delegation recipients.',
    };
  }

  return { allowed: true };
}

// ============================================================================
// Evaluator — CheckMetaAssignment (who decides who decides)
// ============================================================================

/**
 * Evaluates whether a user may reassign (change the owner of) a decision right.
 * This is the meta-level check: "who decides who decides?"
 */
export function checkCanAssignDecisionRight(
  right: DecisionRight,
  context: DecisionRightCheckContext
): DelegationCheckResult {
  const allowed = matchesOwnerBinding(right.metaAssignment, context);
  return {
    allowed,
    reason: allowed ? undefined : 'User does not hold meta-assignment authority for this right.',
  };
}

// ============================================================================
// Owner Binding Matcher (internal)
// ============================================================================

function matchesOwnerBinding(
  owner: DecisionRightOwner,
  context: DecisionRightCheckContext
): boolean {
  switch (owner.bindingType) {
    case 'ROLE_IN_SCOPE':
      return owner.roleId ? context.userRoleIds.includes(owner.roleId) : false;
    case 'PRINCIPAL_IN_SCOPE':
      return owner.principalId ? context.userId === owner.principalId : false;
    case 'ROLE_AND_PRINCIPAL':
      return (
        (owner.principalId ? context.userId === owner.principalId : false) &&
        (owner.roleId ? context.userRoleIds.includes(owner.roleId) : false)
      );
  }
}

// ============================================================================
// Default Rights Factory
// ============================================================================

/**
 * Produces the canonical default decision rights for a new cycle.
 *
 * By default:
 * - The Cycle Owner holds all decision rights with all permitted modes.
 * - The Cycle Owner also holds meta-assignment authority for all rights.
 * - Delegation is not enabled by default (must be explicitly configured).
 *
 * These defaults mirror the existing implicit model and make it explicit.
 */
export function createDefaultDecisionRights(
  cycleId: string,
  cycleOwnerPrincipalId: string,
  configuredBy: string
): DecisionRightsConfiguration {
  const now = new Date();
  const ownerBinding: DecisionRightOwner = {
    bindingType: 'PRINCIPAL_IN_SCOPE',
    principalId: cycleOwnerPrincipalId,
  };

  const domains: CanonicalSessionType[] = [
    'participants',
    'item_pool',
    'criteria',
    'scales',
    'values',
    'items',
  ];

  const domainClassMap: Record<CanonicalSessionType, CanonicalDecisionClassKind[]> = {
    participants: ['selection'],
    item_pool:    ['selection'],
    criteria:     ['selection'],
    scales:       ['range_selection'],
    values:       ['convergence'],
    items:        ['selection', 'force_ranking'],
  };

  const rights: DecisionRight[] = [];
  let idx = 0;

  for (const domain of domains) {
    for (const decisionClass of domainClassMap[domain]) {
      const constraints = DECISION_CLASS_EXECUTION_CONSTRAINTS[decisionClass];
      rights.push({
        id: `default-${domain}-${decisionClass}-${idx++}`,
        domain,
        decisionClass,
        scopeSelector: '*',
        owner: ownerBinding,
        allowedExecutionModes: [...constraints.allowedModes],
        metaAssignment: ownerBinding,
        assignedAt: now,
        assignedBy: configuredBy,
      });
    }
  }

  return {
    cycleId,
    rights,
    delegations: [],
    version: 1,
    configuredAt: now,
    configuredBy,
  };
}
